
import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Axios from "axios";
import AkkhorLayout from "../layout/akkhor";
import { URL_LIST_ALL } from "../configs/api";
import { confirmAlert } from "react-confirm-alert";
import Spinner from "../component/Spinner";
import { Editor } from '@tinymce/tinymce-react';
import { Modal } from "react-bootstrap";
import configData from "../configs/config.json";
import CauHoiRenderer from "./CauHoiRenderer";
import DanhMucCauHoiManager, { buildDanhMucTree } from "./DanhMucCauHoiManager";

const LOAI_OPTIONS = [
    { value: 'single', label: 'Trắc nghiệm 1 đáp án' },
    { value: 'multiple', label: 'Trắc nghiệm nhiều đáp án' },
    { value: 'sort', label: 'Sắp xếp vị trí' },
    { value: 'match', label: 'Nối cột' },
    { value: 'essay', label: 'Tự luận' },
];

// Sinh key duy nhất cho option (không dùng index để tránh lỗi khi xóa)
let _seq = 0;
const newKey = (prefix = 'o') => `${prefix}${Date.now().toString(36)}${(_seq++).toString(36)}`;

const safeParse = (val, fallback) => {
    if (val === null || val === undefined || val === '') return fallback;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch (e) { return fallback; }
};

export default function FormCauHoi() {
    const { user } = useSelector((state) => state.auth);
    const { id } = useParams();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editorMounted, setEditorMounted] = useState(false);
    const editorRef = useRef(null);

    const [loai, setLoai] = useState('single');
    // options dùng cho single/multiple/sort: [{key, text}]
    const [options, setOptions] = useState([{ key: newKey(), text: '' }, { key: newKey(), text: '' }]);
    // single: dapanSingle = key ; multiple: dapanMulti = [key] ; sort: thứ tự đúng = thứ tự của options
    const [dapanSingle, setDapanSingle] = useState('');
    const [dapanMulti, setDapanMulti] = useState([]);
    // match: left/right + map đáp án {leftKey: rightKey}
    const [leftCol, setLeftCol] = useState([{ key: newKey('l'), text: '' }, { key: newKey('l'), text: '' }]);
    const [rightCol, setRightCol] = useState([{ key: newKey('r'), text: '' }, { key: newKey('r'), text: '' }]);
    const [matchMap, setMatchMap] = useState({});

    // Xem trước giao diện học sinh
    const [previewShow, setPreviewShow] = useState(false);
    const [previewAnswer, setPreviewAnswer] = useState(undefined);

    // Danh mục câu hỏi (nhiều, cha-con)
    const [danhMucList, setDanhMucList] = useState([]);
    const [danhMucIds, setDanhMucIds] = useState([]); // mảng id đã chọn
    const [showDanhMuc, setShowDanhMuc] = useState(false);
    const [reloadDanhMuc, setReloadDanhMuc] = useState(false);

    useEffect(() => { setEditorMounted(true); }, []);

    // Load danh sách danh mục
    useEffect(() => {
        Axios.get(URL_LIST_ALL + "learninghub_danhmuc?status=1&limit=500&orderby=position-asc", user.config)
            .then((res) => { if (res.data.status === "success") setDanhMucList(res.data.data || []); })
            .catch((err) => console.log(err));
    }, [user, reloadDanhMuc]);

    useEffect(function () {
        if (id > 0) {
            let mounted = true;
            setLoading(true);
            Axios.get(URL_LIST_ALL + "learninghub_cauhoi/" + id, user.config)
                .then((res) => {
                    setLoading(false);
                    if (res.data.status === "success" && mounted) {
                        const d = res.data.data;
                        setDetail(d);
                        setLoai(d.loai || 'single');
                        if (Array.isArray(d.danhmuc_ids)) setDanhMucIds(d.danhmuc_ids.map(Number));
                        const data = safeParse(d.data, null);
                        const dapan = safeParse(d.dapan, null);
                        if (d.loai === 'match') {
                            setLeftCol(data && data.left ? data.left : leftCol);
                            setRightCol(data && data.right ? data.right : rightCol);
                            setMatchMap(dapan || {});
                        } else if (['single', 'multiple', 'sort'].includes(d.loai)) {
                            setOptions(Array.isArray(data) && data.length ? data : options);
                            if (d.loai === 'single') setDapanSingle(dapan || '');
                            if (d.loai === 'multiple') setDapanMulti(Array.isArray(dapan) ? dapan : []);
                            // sort: thứ tự đúng = dapan (mảng key). Sắp options theo dapan để hiển thị.
                            if (d.loai === 'sort' && Array.isArray(dapan) && Array.isArray(data)) {
                                const ordered = dapan.map(k => data.find(o => o.key === k)).filter(Boolean);
                                if (ordered.length === data.length) setOptions(ordered);
                            }
                        }
                    } else if (res.data.status !== "success") console.log(res.data.message);
                })
                .catch((err) => { console.log(err); setLoading(false); });
            return () => (mounted = false);
        }
    }, [user, id]);// eslint-disable-line

    const formik = useFormik({
        initialValues: detail ? detail : { title: '', loai: 'single', tags: '', status: 1 },
        enableReinitialize: true,
        onSubmit: (values) => {
            // Nội dung lấy trực tiếp từ editor (uncontrolled, tránh nhảy con trỏ).
            const content = editorRef.current?.getContent() || '';
            const plain = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
            if (plain === '') { alert("Vui lòng nhập nội dung câu hỏi"); return; }

            // Build data + dapan theo loại
            let data = null, dapan = null;
            if (loai === 'single' || loai === 'multiple' || loai === 'sort') {
                const cleaned = options.filter(o => (o.text || '').trim() !== '');
                if (cleaned.length < 2) { alert("Cần ít nhất 2 lựa chọn"); return; }
                data = cleaned;
                if (loai === 'single') {
                    if (!dapanSingle || !cleaned.find(o => o.key === dapanSingle)) { alert("Vui lòng chọn 1 đáp án đúng"); return; }
                    dapan = dapanSingle;
                } else if (loai === 'multiple') {
                    const valid = dapanMulti.filter(k => cleaned.find(o => o.key === k));
                    if (valid.length < 1) { alert("Vui lòng chọn ít nhất 1 đáp án đúng"); return; }
                    dapan = valid;
                } else if (loai === 'sort') {
                    // Thứ tự đúng chính là thứ tự hiện tại của options
                    dapan = cleaned.map(o => o.key);
                }
            } else if (loai === 'match') {
                const L = leftCol.filter(o => (o.text || '').trim() !== '');
                const R = rightCol.filter(o => (o.text || '').trim() !== '');
                if (L.length < 1 || R.length < 1) { alert("Cần ít nhất 1 cặp để nối"); return; }
                const map = {};
                L.forEach(l => { if (matchMap[l.key]) map[l.key] = matchMap[l.key]; });
                if (Object.keys(map).length !== L.length) { alert("Vui lòng chọn đáp án nối cho tất cả mục cột trái"); return; }
                data = { left: L, right: R };
                dapan = map;
            }
            // essay: data/dapan = null

            const payload = {
                ...values,
                title: content,
                loai,
                data: data ? JSON.stringify(data) : null,
                dapan: dapan ? JSON.stringify(dapan) : null,
                danhmuc_ids: danhMucIds, // backend after-hook ghi bảng nối
            };

            setLoading(true);
            const done = (res) => {
                setLoading(false);
                if (res.data.status === "success") {
                    if (id > 0) window.location.reload();
                    else window.location.href = '/edit-learninghub_cauhoi/' + res.data.id;
                } else {
                    confirmAlert({ title: 'Lỗi', message: res.data.message, buttons: [{ label: 'OK' }] });
                }
            };
            if (id > 0) Axios.put(URL_LIST_ALL + 'learninghub_cauhoi/' + id, payload, user.config).then(done).catch(err => { console.log(err); setLoading(false); });
            else Axios.post(URL_LIST_ALL + 'learninghub_cauhoi', payload, user.config).then(done).catch(err => { console.log(err); setLoading(false); });
        },
    });

    // ===== Handlers cho options (single/multiple/sort) =====
    const addOption = () => setOptions([...options, { key: newKey(), text: '' }]);
    const removeOption = (key) => {
        setOptions(options.filter(o => o.key !== key));
        if (dapanSingle === key) setDapanSingle('');
        setDapanMulti(dapanMulti.filter(k => k !== key));
    };
    const setOptionText = (key, text) => setOptions(options.map(o => o.key === key ? { ...o, text } : o));
    const moveOption = (idx, dir) => {
        const ni = idx + dir;
        if (ni < 0 || ni >= options.length) return;
        const arr = [...options];
        [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
        setOptions(arr);
    };
    const toggleMulti = (key) => setDapanMulti(dapanMulti.includes(key) ? dapanMulti.filter(k => k !== key) : [...dapanMulti, key]);

    // ===== Handlers cho match =====
    const addPair = () => { setLeftCol([...leftCol, { key: newKey('l'), text: '' }]); setRightCol([...rightCol, { key: newKey('r'), text: '' }]); };
    const setLeftText = (key, text) => setLeftCol(leftCol.map(o => o.key === key ? { ...o, text } : o));
    const setRightText = (key, text) => setRightCol(rightCol.map(o => o.key === key ? { ...o, text } : o));
    const removeLeft = (key) => { setLeftCol(leftCol.filter(o => o.key !== key)); const m = { ...matchMap }; delete m[key]; setMatchMap(m); };
    const removeRight = (key) => {
        setRightCol(rightCol.filter(o => o.key !== key));
        const m = { ...matchMap };
        Object.keys(m).forEach(lk => { if (m[lk] === key) delete m[lk]; });
        setMatchMap(m);
    };

    // Dựng object câu hỏi từ dữ liệu đang nhập để xem trước (giống dữ liệu BE trả cho thí sinh)
    const buildPreviewCauHoi = () => {
        const title = editorRef.current?.getContent() || '';
        let data = null;
        if (loai === 'single' || loai === 'multiple' || loai === 'sort') {
            data = options.filter(o => (o.text || '').trim() !== '');
        } else if (loai === 'match') {
            data = {
                left: leftCol.filter(o => (o.text || '').trim() !== ''),
                right: rightCol.filter(o => (o.text || '').trim() !== ''),
            };
        }
        return { cauhoi_id: 'preview', title, loai, data };
    };

    const openPreview = () => {
        setPreviewAnswer(undefined); // reset câu trả lời mỗi lần mở
        setPreviewShow(true);
    };

    const previewCauHoi = previewShow ? buildPreviewCauHoi() : null;

    const danhMucTree = buildDanhMucTree(danhMucList);
    const toggleDanhMuc = (dmId) => {
        setDanhMucIds((prev) => prev.includes(dmId) ? prev.filter((x) => x !== dmId) : [...prev, dmId]);
    };

    return (
        <AkkhorLayout idrole="learninghub_cauhoi">
            <div className="breadcrumbs-area">
                <ul>
                    <li><Link to="/">Trang chủ</Link></li>
                    <li><Link to="/learninghub_cauhoi">Ngân hàng câu hỏi</Link></li>
                    <li>{id > 0 ? 'Sửa câu hỏi' : 'Tạo câu hỏi'}</li>
                </ul>
            </div>
            <div className="card height-auto mt-5">
                <div className="card-body">
                    <div className="heading-layout1">
                        <div className="item-title"><h3>{id > 0 ? 'Sửa' : 'Tạo'} câu hỏi</h3></div>
                    </div>
                    <form className="new-added-form form-info" method="post" onSubmit={formik.handleSubmit}>
                        <div className="row">
                            <div className="col-12 col-sm-4 form-group">
                                <label>Loại câu hỏi <span className="text-danger">*</span></label>
                                <select className="form-select height32" value={loai} onChange={(e) => setLoai(e.target.value)}>
                                    {LOAI_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div className="col-12 col-sm-4 form-group">
                                <label>Tags</label>
                                <input type="text" placeholder="VD: chương 1, an toàn" className="form-control height32" {...formik.getFieldProps("tags")} autoComplete="off" />
                            </div>
                            <div className="col-12 form-group">
                                <div className="d-flex align-items-center mb-1">
                                    <label className="mb-0">Danh mục câu hỏi</label>
                                    <button type="button" className="btn btn-sm btn-outline-info ml-2" onClick={() => setShowDanhMuc(true)}>
                                        <span className="fas fa-cog"></span> Quản lý danh mục
                                    </button>
                                </div>
                                {danhMucTree.length === 0
                                    ? <div className="text-muted small">Chưa có danh mục. Bấm "Quản lý danh mục" để thêm.</div>
                                    : <div className="border rounded p-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                                        {danhMucTree.map((d) => (
                                            <label key={d.id} className="d-block mb-1" style={{ paddingLeft: d.depth * 18, cursor: 'pointer' }}>
                                                <input type="checkbox" className="mr-2" checked={danhMucIds.includes(d.id)} onChange={() => toggleDanhMuc(d.id)} />
                                                {d.depth > 0 ? '└ ' : ''}{d.title}
                                            </label>
                                        ))}
                                    </div>}
                            </div>
                            <div className="col-12 form-group mt-2">
                                <label>Nội dung câu hỏi <span className="text-danger">*</span></label>
                                {editorMounted && (
                                    <Editor
                                        apiKey={configData.tiny_key}
                                        onInit={(_, editor) => (editorRef.current = editor)}
                                        initialValue={detail ? detail.title : ''}
                                        init={{
                                            height: 250, menubar: false,
                                            plugins: ['advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen', 'insertdatetime', 'media', 'table', 'help', 'wordcount', 'emoticons'],
                                            toolbar: 'undo redo | blocks | bold italic forecolor | bullist numlist | removeformat | image media link | code',
                                            placeholder: 'Nhập nội dung câu hỏi...',
                                        }}
                                    />
                                )}
                            </div>

                            {/* ===== single / multiple / sort ===== */}
                            {['single', 'multiple', 'sort'].includes(loai) &&
                                <div className="col-12 form-group mt-3">
                                    <label>
                                        {loai === 'single' && 'Các lựa chọn (chọn 1 đáp án đúng)'}
                                        {loai === 'multiple' && 'Các lựa chọn (tích các đáp án đúng)'}
                                        {loai === 'sort' && 'Các mục — hãy sắp xếp theo ĐÚNG thứ tự bằng nút lên/xuống'}
                                    </label>
                                    {options.map((o, idx) => (
                                        <div className="d-flex align-items-center mb-2" key={o.key}>
                                            {loai === 'single' &&
                                                <input type="radio" name="dapanSingle" className="mr-2" checked={dapanSingle === o.key} onChange={() => setDapanSingle(o.key)} />}
                                            {loai === 'multiple' &&
                                                <input type="checkbox" className="mr-2" checked={dapanMulti.includes(o.key)} onChange={() => toggleMulti(o.key)} />}
                                            {loai === 'sort' && <span className="mr-2 badge badge-info">{idx + 1}</span>}
                                            <input type="text" className="form-control height32" placeholder={"Lựa chọn " + (idx + 1)} value={o.text} onChange={(e) => setOptionText(o.key, e.target.value)} />
                                            {loai === 'sort' &&
                                                <>
                                                    <button type="button" className="btn btn-sm btn-light ml-1" onClick={() => moveOption(idx, -1)}><span className="fas fa-arrow-up"></span></button>
                                                    <button type="button" className="btn btn-sm btn-light" onClick={() => moveOption(idx, 1)}><span className="fas fa-arrow-down"></span></button>
                                                </>
                                            }
                                            <button type="button" className="btn btn-sm btn-danger ml-1" onClick={() => removeOption(o.key)}><span className="fas fa-times"></span></button>
                                        </div>
                                    ))}
                                    <button type="button" className="btn btn-sm btn-outline-info mt-1" onClick={addOption}><span className="fas fa-plus"></span> Thêm lựa chọn</button>
                                </div>
                            }

                            {/* ===== match ===== */}
                            {loai === 'match' &&
                                <div className="col-12 form-group mt-3">
                                    <label>Nối cột — chọn mục cột phải tương ứng cho mỗi mục cột trái</label>
                                    {leftCol.map((l, idx) => (
                                        <div className="d-flex align-items-center mb-2" key={l.key}>
                                            <input type="text" className="form-control height32" style={{ flex: 2 }} placeholder={"Cột trái " + (idx + 1)} value={l.text} onChange={(e) => setLeftText(l.key, e.target.value)} />
                                            <span className="mx-2">→</span>
                                            <select className="form-select height32" style={{ flex: 2 }} value={matchMap[l.key] || ''} onChange={(e) => setMatchMap({ ...matchMap, [l.key]: e.target.value })}>
                                                <option value="">-- Đáp án đúng --</option>
                                                {rightCol.map((r, ri) => <option key={r.key} value={r.key}>{r.text || ('Cột phải ' + (ri + 1))}</option>)}
                                            </select>
                                            <button type="button" className="btn btn-sm btn-danger ml-1" onClick={() => removeLeft(l.key)}><span className="fas fa-times"></span></button>
                                        </div>
                                    ))}
                                    <hr />
                                    <label>Nội dung các mục cột phải</label>
                                    {rightCol.map((r, idx) => (
                                        <div className="d-flex align-items-center mb-2" key={r.key}>
                                            <input type="text" className="form-control height32" placeholder={"Cột phải " + (idx + 1)} value={r.text} onChange={(e) => setRightText(r.key, e.target.value)} />
                                            <button type="button" className="btn btn-sm btn-danger ml-1" onClick={() => removeRight(r.key)}><span className="fas fa-times"></span></button>
                                        </div>
                                    ))}
                                    <button type="button" className="btn btn-sm btn-outline-info mt-1" onClick={addPair}><span className="fas fa-plus"></span> Thêm cặp</button>
                                </div>
                            }

                            {loai === 'essay' &&
                                <div className="col-12 mt-2"><div className="alert alert-info">Câu tự luận — thí sinh tự viết, giáo viên sẽ chấm tay sau khi nộp bài.</div></div>
                            }

                            <div className="col-12 form-group mt-5">
                                <button type="submit" disabled={formik.isSubmitting} className="btn-fill-lg btn-gradient-yellow btn-hover-bluedark">{loading ? <Spinner /> : <>Lưu lại</>}</button>
                                <button type="button" className="btn btn-info ml-2" onClick={openPreview}><span className="far fa-eye"></span> Xem trước (giao diện học sinh)</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <Modal show={previewShow} onHide={() => setPreviewShow(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Xem trước — giao diện học sinh làm bài</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-secondary py-2">Đây là cách câu hỏi hiển thị khi học sinh làm bài. Bạn có thể thử trả lời để xem tương tác (không lưu).</div>
                    {previewCauHoi &&
                        <div className="border rounded p-3">
                            <div className="d-flex justify-content-between">
                                <strong>Câu hỏi</strong>
                                <span className="badge badge-light">{LOAI_OPTIONS.find(o => o.value === loai)?.label}</span>
                            </div>
                            <div className="mt-1 mb-2" dangerouslySetInnerHTML={{ __html: previewCauHoi.title || '<em class="text-muted">(chưa nhập nội dung)</em>' }} />
                            <CauHoiRenderer
                                cauhoi={previewCauHoi}
                                value={previewAnswer}
                                onChange={(v) => setPreviewAnswer(v)}
                            />
                        </div>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={() => setPreviewShow(false)}>Đóng</button>
                </Modal.Footer>
            </Modal>

            <DanhMucCauHoiManager
                show={showDanhMuc}
                onHide={() => setShowDanhMuc(false)}
                onChanged={() => setReloadDanhMuc((r) => !r)}
            />
        </AkkhorLayout>
    );
}
